/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/productReviewAnalyse.scss';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {ProductReviewAnalyse} from '../../../../util/interfaces';

import {DotsLoader} from '../../../controls/loader/dotsLoader';

import BolmatePlaceholderOverlay from '../../../misc/bolmatePlaceholderOverlay';

import classNames from 'classnames';

import {BolmateButton} from '../../../controls';

import Image from '../../../misc/image';

interface Props {
    onAnalyse: MouseEventHandler;
    loading: Record<ProductReviewAnalyse, boolean>;
    result: Record<ProductReviewAnalyse, string | null>;
}

export default function ProductReviewsAnalyse(props: Props) {
    const {t} = useTranslation();
    const {onAnalyse, loading, result} = props;

    function getAnalysisTitle(analysisType: ProductReviewAnalyse): string {
        return {
            [ProductReviewAnalyse.POSITIVE]: t('reviewAnalytics.productReviews.analyse.positive', 'Positieve punten'),
            [ProductReviewAnalyse.NEGATIVE]: t('reviewAnalytics.productReviews.analyse.negative', 'Negatieve punten'),
            [ProductReviewAnalyse.IMPROVEMENTS]: t(
                'reviewAnalytics.productReviews.analyse.improvements',
                'Verbeterpunten'
            ),
            [ProductReviewAnalyse.TARGET]: t('reviewAnalytics.productReviews.analyse.target', 'Typische doelgroep'),
            [ProductReviewAnalyse.CUSTOMERS]: t('reviewAnalytics.productReviews.analyse.customers', 'Klant persona')
        }[analysisType];
    }

    function renderAnalysis(analysisType: ProductReviewAnalyse, withButton: boolean = false) {
        const content = result[analysisType];
        const isLoading = loading[analysisType];
        return (
            <div className='analysis-container'>
                <div className='analysis-header'>
                    <label className='analysis-title'>BolmateAI - {getAnalysisTitle(analysisType)}</label>
                </div>
                <div className='analysis-content'>
                    {!content && (
                        <BolmatePlaceholderOverlay
                            className={classNames('analysis-overlay', (withButton || isLoading) && 'without-image')}
                        />
                    )}
                    {!content && !isLoading && withButton && renderButton()}
                    {isLoading && <DotsLoader dotSize='4rem' className='analysis-loader' noSmile />}
                    {/* TODO: XSS risk — `content` is a raw HTML string from the AI/LLM backend response
                        and is injected without sanitization. Run through DOMPurify.sanitize() or render
                        as plain text / safe markdown before injecting. */}
                    {!!content && <div className='analysis-result' dangerouslySetInnerHTML={{__html: content}} />}
                </div>
            </div>
        );
    }

    function renderButton() {
        return (
            <BolmateButton
                onClick={onAnalyse}
                label={t('reviewAnalytics.productReviews.analyse.analyseWithAI', 'Analyseren met BolmateAI')}
                className='btn-analyse'
                ornaments={{
                    element: (
                        <Image
                            name='logo-no-text'
                            alt=''
                            style={{
                                maxWidth: '1.2rem',
                                maxHeight: '1.2rem',
                                objectFit: 'scale-down'
                            }}
                        />
                    )
                }}
                secondary
            />
        );
    }

    return (
        <div className='product-review-analysis'>
            {renderAnalysis(ProductReviewAnalyse.POSITIVE, true)}
            {renderAnalysis(ProductReviewAnalyse.NEGATIVE)}
            {renderAnalysis(ProductReviewAnalyse.IMPROVEMENTS)}
            {renderAnalysis(ProductReviewAnalyse.TARGET)}
            {renderAnalysis(ProductReviewAnalyse.CUSTOMERS)}
        </div>
    );
}
